import { Components, Theme } from '@mui/material/styles';
import { colors, success, error, warning, brand, greyLightMode } from './colors';
import { typography } from './typography';
import { spacing } from './spacing';

export const getComponents = (): Components<Omit<Theme, 'components'>> => {
  const borderColor = colors.divider;
  const hoverBorderColor = colors.grey[400];
  const fieldsetBorderColor = colors.grey[300];
  const tableHeaderBg = colors.background.default;
  const tooltipBg = colors.grey[800];
  const tooltipColor = colors.common.white;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
          margin: 0,
          padding: 0,
        },
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
          width: '100%',
          backgroundColor: colors.background.default,
        },
        '#root': {
          height: '100%',
          width: '100%',
        },
        a: {
          textDecoration: 'none',
          color: 'inherit',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: colors.background.light,
        },
        '::-webkit-scrollbar-thumb': {
          background: colors.grey[300],
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: colors.grey[400],
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: typography.button?.textTransform,
          fontWeight: typography.button?.fontWeight,
          padding: `${spacing.sm}px ${spacing.lg}px`,
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
        sizeLarge: {
          padding: `${spacing.md}px ${spacing['2xl']}px`,
          fontSize: typography.h6?.fontSize,
        },
        sizeSmall: {
          padding: `${spacing.xs}px ${spacing.md}px`,
          fontSize: '0.8125rem',
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: fieldsetBorderColor,
            },
            '&:hover fieldset': {
              borderColor: hoverBorderColor,
            },
            '&.Mui-focused fieldset': {
              borderWidth: 1.5,
            },
          },
        },
      },
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${borderColor}`,
          backgroundColor: colors.background.paper,
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacing.lg,
          '&:last-child': {
            paddingBottom: spacing.lg,
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${borderColor}`,
          backgroundColor: colors.background.paper,
        },
      },
      defaultProps: {
        color: 'inherit',
        elevation: 0,
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 64,
          '@media (min-width: 600px)': {
            minHeight: 64,
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${borderColor}`,
          backgroundColor: colors.background.paper,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundColor: colors.background.paper,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: typography.h5?.fontSize,
          fontWeight: typography.h5?.fontWeight,
          padding: `${spacing.xl}px ${spacing['2xl']}px`,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: `0 ${spacing['2xl']}px ${spacing.xl}px`,
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${spacing.lg}px ${spacing['2xl']}px`,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        sizeSmall: {
          height: 24,
          fontSize: typography.caption?.fontSize,
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: tableHeaderBg,
          '& .MuiTableCell-root': {
            fontWeight: 600,
            color: colors.text.primary,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: borderColor,
          padding: `${spacing.md}px ${spacing.lg}px`,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minWidth: 'auto',
          padding: `${spacing.md}px ${spacing.lg}px`,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: success.shade2,
        },
        standardError: {
          backgroundColor: error.shade2,
        },
        standardWarning: {
          backgroundColor: warning.shade2,
        },
        standardInfo: {
          backgroundColor: brand.shade2,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tooltipBg,
          color: tooltipColor,
          fontSize: typography.caption?.fontSize,
          padding: `6px ${spacing.md}px`,
          borderRadius: 4,
        },
      },
    },

    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'all 0.2s ease',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 12,
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
          backgroundColor: greyLightMode.shade4,
        },
      },
    },

    MuiCircularProgress: {
      defaultProps: {
        size: 40,
        thickness: 4,
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          border: `1px solid ${borderColor}`,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: brand.shade3,
            '&:hover': {
              backgroundColor: brand.shade4,
            },
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldsetBorderColor,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: hoverBorderColor,
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: borderColor,
        },
      },
    },

    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: brand.shade3,
            '&:hover': {
              backgroundColor: brand.shade4,
            },
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
        },
        switchBase: {
          padding: 0,
          margin: 2,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: colors.common.white,
            '& + .MuiSwitch-track': {
              backgroundColor: brand.shade7,
              opacity: 1,
              border: 0,
            },
          },
        },
        thumb: {
          boxSizing: 'border-box',
          width: 22,
          height: 22,
        },
        track: {
          borderRadius: 26 / 2,
          backgroundColor: colors.grey[300],
          opacity: 1,
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        colorError: {
          backgroundColor: error.shade7,
        },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          border: `1px solid ${borderColor}`,
        },
        option: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&[aria-selected="true"]': {
            backgroundColor: brand.shade3,
          },
        },
      },
    },
  };
};

export const components = getComponents();
